/** @format */

export type Format = 'csv' | 'json'
export type Folder = 'inbox' | 'spam'

// Maximum submissions allowed for selected mode (matches backend validation)
export const MAX_SELECTED_SUBMISSIONS = 10000

// Export option types (frontend - camelCase)
export type ExportOption =
  | {mode: 'folder'; folder: Folder}
  | {mode: 'selected'; submissionIds: string[]}
  | {mode: 'search'; query: string}

// Frontend state for export modal
export type ExportModalState = {
  format: Format
  exportOptions: ExportOption[]
}

// API payload types (snake_case for backend - matches Pydantic models)
export type ExportOptionPayload =
  | {mode: 'folder'; folder: Folder}
  | {mode: 'selected'; submission_ids: string[]}
  | {mode: 'search'; query: string}

export type ExportRequestPayload = {
  format: Format
  export_options: ExportOptionPayload[]
}

// Helper to convert frontend option to API payload option
function toExportOptionPayload(option: ExportOption): ExportOptionPayload {
  switch (option.mode) {
    case 'folder':
      return {mode: 'folder', folder: option.folder}
    case 'selected':
      return {mode: 'selected', submission_ids: option.submissionIds}
    case 'search':
      return {mode: 'search', query: option.query}
  }
}

// Helper to convert full export state to API payload
export function toExportPayload(state: ExportModalState): ExportRequestPayload {
  return {
    format: state.format,
    export_options: state.exportOptions.map(toExportOptionPayload),
  }
}

// Helper to create folder export options
export function createFolderOptions(folders: {
  inbox: boolean
  spam: boolean
}): Array<{mode: 'folder'; folder: Folder}> {
  const options: Array<{mode: 'folder'; folder: Folder}> = []
  if (folders.inbox) {
    options.push({mode: 'folder', folder: 'inbox'})
  }
  if (folders.spam) {
    options.push({mode: 'folder', folder: 'spam'})
  }
  return options
}

// Validation helper
export function validateExportOptions(options: ExportOption[]): string | null {
  if (options.length === 0) {
    return 'At least one export option must be selected'
  }

  for (const option of options) {
    if (option.mode === 'selected') {
      if (option.submissionIds.length === 0) {
        return 'Selected submissions cannot be empty'
      }
      if (option.submissionIds.length > MAX_SELECTED_SUBMISSIONS) {
        return `Cannot export more than ${MAX_SELECTED_SUBMISSIONS} submissions at once`
      }
    }
    if (option.mode === 'search') {
      if (!option.query || !option.query.trim()) {
        return 'Search query cannot be empty'
      }
    }
  }

  return null
}
